import { Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon } from '@persona-id/design-tokens/icons/24px';
import { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { printMixins } from 'dashboard/lib/printMixins';
import Card from 'design-system/components/Card';
import { TransitionDuration } from 'design-system/variables/transitions';
import useToggle from 'lib/hooks/useToggle';

const Tab = styled('div')<{ disabled: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${Spacing[4]} ${Spacing[5]};
  ${({ disabled }) =>
    !disabled &&
    css`
      cursor: pointer;
    `}
`;

const HeaderContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActionContainer = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface ToggleProps {
  toggled: boolean;
}

const Toggle = styled('div')<ToggleProps>`
  transform-origin: 50% 50%;
  transform: ${({ toggled }) => (toggled ? 'rotate(0deg)' : 'rotate(-180deg)')};
  transition: transform ${TransitionDuration.Fast};

  display: flex;
  align-items: center;

  ${printMixins.hide}
`;

const Detail = styled('div')<{ show: boolean }>`
  padding: 20px ${Spacing[3]} ${Spacing[3]};
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
  transition: ${TransitionDuration.Fast} ease;
  overflow: auto;

  ${({ show }) =>
    !show &&
    css`
      height: 0;
      padding-top: 0;
      padding-bottom: 0;
      border: 0px transparent;
      visibility: hidden;
    `}
`;

interface ClickableProps {
  children: ReactNode;
  disabled?: boolean;
  header: ReactNode;
  subheader?: ReactNode;
  dropdownMenu?: ReactNode;
  isDefaultOpen: boolean;
}

const ClickableDetail = ({
  children,
  disabled,
  header,
  subheader,
  dropdownMenu,
  isDefaultOpen,
}: ClickableProps) => {
  const [showDetails, { toggle }] = useToggle(isDefaultOpen);

  return (
    <Card>
      <Tab
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        disabled={disabled}
        onClick={disabled ? undefined : toggle}
        role={disabled ? undefined : 'button'}
        tabIndex={disabled ? undefined : 0}
      >
        <HeaderContainer>
          {header}

          {!disabled && (
            <ActionContainer>
              {dropdownMenu && dropdownMenu}

              <Toggle toggled={showDetails}>
                <CaretDownIcon />
              </Toggle>
            </ActionContainer>
          )}
        </HeaderContainer>

        {subheader && subheader}
      </Tab>

      <Detail show={showDetails}>{children}</Detail>
    </Card>
  );
};

export default ClickableDetail;
